import type { LabelerTaxonomy } from './types'

/**
 * Helpers over the taxonomy served by fim-service (`/labeler/taxonomy`).
 * The map itself is never duplicated here — fim-service and the pipeline
 * already carry parity-tested twins.
 */

function subtypeKind(
  taxonomy: LabelerTaxonomy | undefined,
  type: string
): string | null {
  for (const [kind, subs] of Object.entries(taxonomy?.subtypes ?? {})) {
    if (subs.includes(type)) return kind
  }
  return null
}

export function canonicalizeType(
  taxonomy: LabelerTaxonomy | undefined,
  raw: string | null | undefined
): string | null {
  if (!taxonomy || !raw) return null
  const mapped = taxonomy.aliases[raw] ?? raw
  if (mapped in taxonomy.kind_of_type) return mapped
  return subtypeKind(taxonomy, mapped) ? mapped : null
}

export function kindOfType(
  taxonomy: LabelerTaxonomy | undefined,
  type: string | null | undefined
): string | null {
  if (!taxonomy || !type) return null
  return taxonomy.kind_of_type[type] ?? subtypeKind(taxonomy, type)
}

export function typesByKind(
  taxonomy: LabelerTaxonomy | undefined
): Record<string, string[]> {
  if (!taxonomy) return {}
  const grouped: Record<string, string[]> = {}
  for (const [type, kind] of Object.entries(taxonomy.kind_of_type)) {
    ;(grouped[kind] ??= []).push(type)
  }
  for (const [kind, subs] of Object.entries(taxonomy.subtypes ?? {})) {
    ;(grouped[kind] ??= []).push(...subs)
  }
  for (const [kind, types] of Object.entries(grouped)) {
    types.sort((a, b) => {
      if (a === kind) return -1
      if (b === kind) return 1
      return a.localeCompare(b)
    })
  }
  return grouped
}

export function typeLabel(type: string | null): string {
  if (!type) return 'Untyped'
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * One green for ALL typed boxes regardless of kind — kind is read from the
 * sidebar, not the sheet. An unconfirmed seed is marked by a dashed border,
 * never a colour of its own.
 */
export const TYPED_COLOR = {
  stroke: 'rgba(34, 197, 94, 0.7)',
  fill: 'rgba(34, 197, 94, 0.1)',
}

export const UNTYPED_COLOR = {
  stroke: 'rgba(234, 179, 8, 0.6)',
  fill: 'rgba(234, 179, 8, 0.05)',
}

export const SELECTED_COLOR = {
  stroke: '#ef4444',
  fill: 'rgba(239, 68, 68, 0.15)',
}

export const BULK_SELECTED_COLOR = {
  stroke: 'rgba(59, 130, 246, 0.8)',
  fill: 'rgba(59, 130, 246, 0.2)',
}

export interface BoxPaint {
  stroke: string
  fill: string
  dashed: boolean
}

export function colorForBox(
  box: { type: string | null; verified: boolean; origin: 'seeded' | 'drawn' },
  state?: { selected?: boolean; bulkSelected?: boolean }
): BoxPaint {
  const dashed = box.origin === 'seeded' && !box.verified
  if (state?.selected) return { ...SELECTED_COLOR, dashed }
  if (state?.bulkSelected) return { ...BULK_SELECTED_COLOR, dashed }
  const base = box.type ? TYPED_COLOR : UNTYPED_COLOR
  return { ...base, dashed }
}

/** Read-only prediction ghosts (page not seeded yet). */
export function predictionColor(
  taxonomy: LabelerTaxonomy | undefined,
  rawType: string | null
): BoxPaint {
  const type = canonicalizeType(taxonomy, rawType)
  return { ...(type ? TYPED_COLOR : UNTYPED_COLOR), dashed: true }
}
